import { useQuery } from "@tanstack/react-query";
import { Calculator, ChevronDown, Download, FileText, Search } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router";
import { Input, Logo, Skeleton } from "@/components/ui";
import { api, type SalesRoom } from "@/lib/api";
import { fillSalesPlaceholders, pickPackEstimate, type SalesLang } from "@logiful/domain";

/** Texte der Angebotsseite – Sprache aus dem Deal, umschaltbar (unabhängig von der Portalsprache) */
const T = {
  de: {
    yourOffer: "Ihr Angebot", pdf: "Ihr Angebot als PDF", priceNote: "Alle Preise netto zzgl. USt. Die Abrechnung erfolgt verbrauchsabhängig nach den oben genannten Konditionen.",
    noOffer: "Ihr Angebot wird gerade vorbereitet – wir melden uns in Kürze.", calculator: "Pick-&-Pack-Rechner", calcIntro: "Simulieren Sie Ihre voraussichtlichen monatlichen Pick-&-Pack-Gebühren.",
    packages: "Anzahl Pakete (pro Monat)", items: "Artikel je Paket (Ø)", packFlat: "Packpauschale", perPackage: "je Paket", pick: "Pick", perItem: "je Artikel", monthly: "Voraussichtlich pro Monat",
    calcNote: "Unverbindliche Schätzung auf Basis der aktuellen Konditionen. Preise netto zzgl. USt. Verpackungsmaterial, Versand, Warehousing und Retouren werden separat abgerechnet.",
    faq: "Häufige Fragen", faqSearch: "Fragen durchsuchen …", faqNone: "Keine passenden Fragen gefunden.",
    notFound: "Angebot nicht verfügbar", notFoundHint: "Dieser Link ist ungültig oder nicht mehr aktiv. Bitte wenden Sie sich an Ihren Ansprechpartner bei Logiful.",
    soon: "Vertrag und Kundenkonto: Ihr Ansprechpartner meldet sich bei Ihnen. In Kürze können Sie hier auch direkt Kunde werden.",
    date: "vom",
  },
  en: {
    yourOffer: "Your offer", pdf: "Your offer as PDF", priceNote: "All prices net plus VAT. Billing is usage-based according to the conditions listed above.",
    noOffer: "Your offer is being prepared – we'll be in touch shortly.", calculator: "Pick & pack calculator", calcIntro: "Estimate your expected monthly pick & pack fees.",
    packages: "Parcels (per month)", items: "Items per parcel (avg.)", packFlat: "Packing flat rate", perPackage: "per parcel", pick: "Pick", perItem: "per item", monthly: "Expected per month",
    calcNote: "Non-binding estimate based on current conditions. Prices net plus VAT. Packaging, shipping, warehousing and returns are billed separately.",
    faq: "Frequently asked questions", faqSearch: "Search questions …", faqNone: "No matching questions found.",
    notFound: "Offer not available", notFoundHint: "This link is invalid or no longer active. Please contact your Logiful representative.",
    soon: "Contract and customer account: your representative will get in touch. Soon you'll be able to become a customer right here.",
    date: "dated",
  },
} as const;

const euro = (v: number, l: SalesLang) => new Intl.NumberFormat(l === "en" ? "en-GB" : "de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const num = (s: string) => Math.max(0, Number(s.replace(",", ".")) || 0);

/** Sales Room für Interessenten (Link aus der Angebotsmail) – ohne Anmeldung, Inhalte aus dem Retool-„Sales Deal Manager“ */
export function SalesRoomPage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const q = useQuery({ queryKey: ["sales-room", token], queryFn: () => api.salesRoom.get(token), retry: false });
  const [langOverride, setLang] = useState<SalesLang | null>(null);
  const l: SalesLang = langOverride ?? q.data?.language ?? "de";
  const t = T[l];

  // Klick aus der Angebotsmail protokollieren (einmal je Aufruf)
  const et = params.get("et");
  useEffect(() => {
    if (et && token) void api.salesRoom.opened(token, et).catch(() => undefined);
  }, [et, token]);
  useEffect(() => {
    document.title = q.data ? `${T[l].yourOffer} – Logiful` : "Logiful";
  }, [q.data, l]);

  if (q.isLoading)
    return (
      <Frame>
        <Skeleton className="h-28" />
        <Skeleton className="h-64" />
      </Frame>
    );
  if (!q.data)
    return (
      <Frame>
        <Card>
          <h1 className="text-[18px] font-semibold">{t.notFound}</h1>
          <p className="mt-2 text-[14px] text-fg-muted">{t.notFoundHint}</p>
        </Card>
      </Frame>
    );

  const d = q.data;
  const welcome = d.welcome[l];
  const fill = (s: string) => fillSalesPlaceholders(s, { contactName: d.deal.contactName, company: d.deal.company, title: d.offer?.title ?? d.deal.title });

  return (
    <Frame
      lang={l}
      onLang={setLang}
      logos={d.deal.customerLogoUrl ? <><img src={d.deal.customerLogoUrl} alt={d.deal.company ?? ""} className="h-12 w-auto max-w-[200px] object-contain" referrerPolicy="no-referrer" /><span className="text-2xl font-light text-fg-subtle">×</span></> : null}
    >
      {d.blocks.welcome && (
        <Card className="text-center py-8">
          {welcome.badge.trim() && <span className="inline-block rounded-full bg-coral/10 px-2.5 py-0.5 text-[12px] font-medium text-coral">{welcome.badge}</span>}
          <h1 className="mt-3 text-[26px] font-semibold tracking-tight">{fill(welcome.title)}</h1>
          {welcome.subtitle.trim() && <p className="mt-2 text-fg-muted">{fill(welcome.subtitle)}</p>}
        </Card>
      )}

      {d.blocks.offer && <OfferBlock d={d} l={l} token={token} />}
      {d.blocks.calculator && <CalculatorBlock d={d} l={l} />}
      {d.blocks.becomeCustomer && d.offer && <Card><p className="text-[14px] text-fg-muted">{t.soon}</p></Card>}
      {d.blocks.faq && d.faqs && <FaqBlock items={d.faqs[l].length ? d.faqs[l] : d.faqs.de} l={l} />}
    </Frame>
  );
}

function Frame({ children, lang, onLang, logos }: { children: ReactNode; lang?: SalesLang; onLang?: (l: SalesLang) => void; logos?: ReactNode }) {
  return (
    <div className="min-h-screen bg-bg">
      {onLang && (
        <div className="border-b border-border bg-surface">
          <div className="mx-auto flex max-w-3xl justify-end px-4 py-2">
            <div className="flex gap-0.5 rounded-lg border border-border p-0.5 text-[12.5px] font-medium">
              {(["de", "en"] as const).map((x) => (
                <button key={x} type="button" aria-pressed={lang === x} onClick={() => onLang(x)} className={`rounded-md px-2.5 py-1 uppercase ${lang === x ? "bg-ink text-ink-fg" : "text-fg-muted hover:text-fg"}`}>
                  {x}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-3xl px-4 py-6 space-y-5">
        <div className="flex items-center justify-center gap-5 py-3">
          {logos}
          <Logo />
        </div>
        {children}
      </div>
    </div>
  );
}

const Card = ({ children, className = "" }: { children: ReactNode; className?: string }) => <section className={`rounded-2xl border border-border bg-surface p-6 ${className}`}>{children}</section>;

function Collapsible({ title, icon, open, onToggle, children }: { title: string; icon?: ReactNode; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <Card>
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between text-left">
        <span className="flex items-center gap-2 text-[17px] font-semibold">{icon}{title}</span>
        <ChevronDown className={`size-5 text-fg-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </Card>
  );
}

function OfferBlock({ d, l, token }: { d: SalesRoom; l: SalesLang; token: string }) {
  const t = T[l];
  const [open, setOpen] = useState(true);
  if (!d.offer)
    return (
      <Card>
        <h2 className="text-[17px] font-semibold">{t.yourOffer}</h2>
        <p className="mt-2 text-[14px] text-fg-muted">{t.noOffer}</p>
      </Card>
    );
  const groups = d.offer.groups[l];
  return (
    <Collapsible title={t.yourOffer} open={open} onToggle={() => setOpen(!open)}>
      <p className="-mt-2 mb-4 text-[13px] text-fg-muted">
        {d.offer.title} · {t.date} {new Date(d.offer.date).toLocaleDateString(l === "en" ? "en-GB" : "de-DE")}
      </p>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.title}>
            <h3 className="text-[14px] font-semibold">{g.title}</h3>
            {g.description && <p className="text-[12.5px] text-fg-muted">{g.description}</p>}
            <div className="mt-2 divide-y divide-border rounded-xl border border-border">
              {g.items.map((it, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4 px-3 py-2 text-[14px]">
                  <span className="min-w-0">
                    {it.label}
                    {it.unit && <span className="block text-[12px] text-fg-muted">{it.unit}</span>}
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">{it.prefix ? `${it.prefix} ` : ""}{euro(it.price, l)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="text-[12px] text-fg-muted">{t.priceNote}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-2/60 p-3">
          <span className="flex items-center gap-2 text-[14px]"><FileText className="size-4 text-coral" />{t.pdf}</span>
          <span className="flex gap-2">
            {(["de", "en"] as const).map((x) => (
              <a key={x} href={api.salesRoom.pdfUrl(token, x)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-[13px] font-medium hover:bg-surface-2">
                <Download className="size-3.5" /> {x === "de" ? "Deutsch" : "English"}
              </a>
            ))}
          </span>
        </div>
      </div>
    </Collapsible>
  );
}

function CalculatorBlock({ d, l }: { d: SalesRoom; l: SalesLang }) {
  const t = T[l];
  const [open, setOpen] = useState(false);
  const [packages, setPackages] = useState(d.deal.packagesPerMonth ? String(d.deal.packagesPerMonth) : "");
  const [items, setItems] = useState("");
  const r = useMemo(() => pickPackEstimate(num(packages), num(items), d.calculator), [packages, items, d.calculator]);
  const active = num(packages) > 0;
  const clean = (s: string) => s.replace(/[^0-9.,]/g, "");
  return (
    <Collapsible title={t.calculator} icon={<Calculator className="size-5 text-coral" />} open={open} onToggle={() => setOpen(!open)}>
      <p className="mb-4 text-[14px] text-fg-muted">{t.calcIntro}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">{t.packages}</span>
          <Input className="mt-1" inputMode="numeric" value={packages} onChange={(e) => setPackages(clean(e.target.value))} placeholder="1500" />
        </label>
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">{t.items}</span>
          <Input className="mt-1" inputMode="decimal" value={items} onChange={(e) => setItems(clean(e.target.value))} placeholder={l === "en" ? "2.5" : "2,5"} />
        </label>
      </div>
      <div className="mt-5 space-y-2 rounded-xl bg-surface-2/60 p-4 text-[14px]">
        <div className="flex justify-between"><span className="text-fg-muted">{t.packFlat} · {euro(d.calculator.pack, l)} {t.perPackage}</span><span className="tabular-nums">{active ? euro(r.packCost, l) : "—"}</span></div>
        <div className="flex justify-between"><span className="text-fg-muted">{t.pick} · {euro(d.calculator.pick, l)} {t.perItem}</span><span className="tabular-nums">{active ? euro(r.pickCost, l) : "—"}</span></div>
        <div className="flex justify-between border-t border-border pt-2 text-[15px] font-semibold"><span>{t.monthly}</span><span className="tabular-nums">{active ? euro(r.monthly, l) : "—"}</span></div>
      </div>
      <p className="mt-3 text-[12px] text-fg-muted">{t.calcNote}</p>
    </Collapsible>
  );
}

function FaqBlock({ items, l }: { items: { q: string; a: string }[]; l: SalesLang }) {
  const t = T[l];
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const s = query.trim().toLowerCase();
  const list = s ? items.filter((f) => f.q.toLowerCase().includes(s) || f.a.toLowerCase().includes(s)) : items;
  if (!items.length) return null;
  return (
    <Card>
      <h2 className="mb-3 text-[17px] font-semibold">{t.faq}</h2>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t.faqSearch} className="pl-9" />
      </div>
      {list.length ? (
        <div className="divide-y divide-border">
          {list.map((f, i) => (
            <div key={f.q}>
              <button type="button" onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i} className="flex w-full items-center justify-between gap-3 py-3 text-left text-[14px] font-medium">
                {f.q}
                <ChevronDown className={`size-4 shrink-0 text-fg-muted transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-3 text-[14px] text-fg-muted whitespace-pre-line">{f.a}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[14px] text-fg-muted">{t.faqNone}</p>
      )}
    </Card>
  );
}
